import { ItemCell } from '../../components/table-cells';
import { useItemPictures } from '../../hooks/useItemPictures';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Card,
  Spin,
  Row,
  Col,
  Divider,
  Modal,
  InputNumber,
  Input,
  message,
  Timeline,
  Tooltip,
  Alert,
  Statistic,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  RollbackOutlined,
  SendOutlined,
  DollarOutlined,
  DeleteOutlined,
  PrinterOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getGRN, createGRNReturn, deleteGRN, getReceiptProofLink, getReceiptCases, getReturnableLines, retryReceiptStep, ReceiptCase, ReceiptCases, ReturnableLine } from '../../api';
import { money, OUTCOME_LABEL, PATH_LABEL, PAYMENT_LABEL } from '../../components/receiving/shared';
import { CaseCell, caseSummary, ChooseOutcomeWindow } from '../../components/receiving/ReceiptCases';
import ShipWindow, { ShipTarget } from '../../components/returns/ShipWindow';
import SettleWindow, { SettleTarget } from '../../components/returns/SettleWindow';
import ReturnGoodsWindow from '../../components/returns/ReturnGoodsWindow';
import { buildGrnSlipHtml } from '../../utils/grnSlip';
import { useAuth } from '../../context/AuthContext';
import EntityHistory from '../../components/EntityHistory';
import ResponsiveTable from '../../components/responsive/ResponsiveTable';

const { Title, Text } = Typography;

const RETURN_STATUS_COLORS: Record<string, string> = {
  Pending: 'orange',
  Shipped: 'blue',
  Credited: 'green',
  Refunded: 'cyan',
};

const GRNView: React.FC = () => {
  const { grnId } = useParams<{ grnId: string }>();
  const navigate = useNavigate();
  const { isProcurement, isAdmin, user, canAccess } = useAuth();
  const [loading, setLoading] = useState(true);
  const [grn, setGrn] = useState<any>(null);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returningLine, setReturningLine] = useState<any>(null);
  const [returnForm, setReturnForm] = useState({ returnQty: 0, returnReason: '', notes: '' });
  const [returnLoading, setReturnLoading] = useState(false);
  // Rejected goods step 2 (2026-10-25): one Ship and one Settle action everywhere (shared with Returns & credits).
  const [shipTarget, setShipTarget] = useState<ShipTarget | null>(null);
  const [settleTarget, setSettleTarget] = useState<SettleTarget | null>(null);
  // Rejected goods step 1 (2026-10-24): where each rejected line stands (receipts saved by the new window only).
  const [caseData, setCaseData] = useState<ReceiptCases | null>(null);
  const [choosing, setChoosing] = useState<ReceiptCase | null>(null);
  // Step 4a (mockup "Door & returns" 2–3): what each line can still return, and the line being returned (Return goods).
  const [returnable, setReturnable] = useState<Map<number, ReturnableLine> | null>(null);
  const [returnGoods, setReturnGoods] = useState<ReturnableLine | null>(null);
  // Item pictures (2026-09-22): the receiver checks the delivery against the picture on the line.
  // Declared here because this page returns early while it loads.
  const lineItemIds = useMemo(
    () => (grn?.lines || []).map((l: any) => l.poLine?.mrLine?.catalogItem?.id ?? null),
    [grn]
  );
  const { pictures: linePictures, reload: reloadLinePicture } = useItemPictures(lineItemIds);
  // 2026-10-22: proof photos show as small thumbnails (a fresh link per picture; other files stay a link).
  const [proofThumbs, setProofThumbs] = useState<Record<number, string>>({});
  useEffect(() => {
    const files: any[] = Array.isArray(grn?.proofFiles) ? grn.proofFiles : [];
    setProofThumbs({});
    if (!grn?.grnId) return;
    let alive = true;
    files.forEach((f, i) => {
      if (!String(f?.mime || '').startsWith('image/')) return;
      getReceiptProofLink(grn.grnId, i)
        .then((url) => { if (alive && url) setProofThumbs((t) => ({ ...t, [i]: url })); })
        .catch(() => undefined);
    });
    return () => { alive = false; };
  // Keyed on the receipt and its files, so a refetch after a return does not reload the thumbnails.
  }, [grn?.grnId, JSON.stringify(grn?.proofFiles || [])]); // eslint-disable-line react-hooks/exhaustive-deps
  const fetchGRN = useCallback(async () => {
    if (!grnId) return;
    setLoading(true);
    try {
      // A server without the cases endpoint answers 404: the page then shows the receipt as before.
      const [data, cases, ret] = await Promise.all([getGRN(grnId), getReceiptCases(grnId).catch(() => null), getReturnableLines({ grnId }).catch(() => null)]);
      setGrn(data);
      setCaseData(cases);
      setReturnable(ret ? new Map(ret.map((r) => [r.grnLineId, r])) : null);
    } catch (err) {
      message.error('Failed to load GRN');
    } finally {
      setLoading(false);
    }
  }, [grnId]);

  // After a case action: reload the receipt and its cases without the full-page spinner.
  const refreshCases = useCallback(async () => {
    if (!grnId) return;
    try {
      const [data, cases, ret] = await Promise.all([getGRN(grnId), getReceiptCases(grnId), getReturnableLines({ grnId }).catch(() => null)]);
      setGrn(data);
      setCaseData(cases);
      setReturnable(ret ? new Map(ret.map((r) => [r.grnLineId, r])) : null);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Could not reload the receipt');
    }
  }, [grnId]);

  const retryStep = async (c: ReceiptCase, step: 'return' | 'request' | 'short_close') => {
    try {
      const res = await retryReceiptStep(c.grnLineId, step);
      message.success(step === 'return' ? `Return ${res.ref || ''} created`.replace('  ', ' ') : step === 'short_close' ? 'Short-closed on the PO line' : 'Sent to Ahmed (Cancellations)');
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Could not do it now');
    }
    await refreshCases();
  };

  useEffect(() => {
    fetchGRN();
  }, [fetchGRN]);

  const fmtNum = (val: any) =>
    val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-';

  const openReturnModal = (line: any) => {
    const existingReturns = (line.returns || []).reduce((s: number, r: any) => s + Number(r.returnQty), 0);
    const maxReturn = Number(line.rejectedQty) - existingReturns;
    setReturningLine({ ...line, maxReturn });
    setReturnForm({ returnQty: maxReturn, returnReason: '', notes: '' });
    setReturnModalOpen(true);
  };

  const handleCreateReturn = async () => {
    if (!returningLine) return;
    if (returnForm.returnQty <= 0) { message.warning('Enter return quantity'); return; }
    if (!returnForm.returnReason.trim()) { message.warning('Enter return reason'); return; }
    setReturnLoading(true);
    try {
      await createGRNReturn(returningLine.id, {
        returnQty: returnForm.returnQty,
        returnReason: returnForm.returnReason,
        notes: returnForm.notes || undefined,
      });
      message.success('Return created successfully');
      setReturnModalOpen(false);
      fetchGRN();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create return');
    } finally {
      setReturnLoading(false);
    }
  };

  const handleDeleteGRN = () => {
    Modal.confirm({
      title: 'Delete GRN?',
      content: `This will permanently delete ${grnId} and reverse all received quantities on the PO. This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteGRN(grnId!);
          message.success('GRN deleted');
          navigate('/grns');
        } catch (err: any) {
          message.error(err.response?.data?.error || 'Failed to delete GRN');
        }
      },
    });
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!grn) return <div>GRN not found</div>;

  const lines = grn.lines || [];
  const totalDelivered = lines.reduce((s: number, l: any) => s + Number(l.deliveredQty), 0);
  const totalAccepted = lines.reduce((s: number, l: any) => s + Number(l.acceptedQty), 0);
  const totalRejected = lines.reduce((s: number, l: any) => s + Number(l.rejectedQty), 0);
  const qualityPct = totalDelivered > 0 ? Math.round((totalAccepted / totalDelivered) * 100) : 100;
  const hasRejections = totalRejected > 0;
  // GRN phase 1 (2026-10-21): totals per unit and in money, line value and "Goes to".
  const priceOf = (l: any) => Number(l.poLine?.actualUnitPrice ?? l.poLine?.unitPrice ?? 0);
  const perUnit = (field: 'acceptedQty' | 'rejectedQty') => {
    const m = new Map<string, number>();
    lines.forEach((l: any) => { const v = Number(l[field] || 0); if (v) m.set(l.poLine?.unit?.name || '', (m.get(l.poLine?.unit?.name || '') || 0) + v); });
    return Array.from(m.entries()).map(([u, v]) => `${v.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${u}`.trim()).join(' · ') || '0';
  };
  const receiptValue = lines.reduce((s: number, l: any) => s + Number(l.acceptedQty) * priceOf(l), 0);
  const proofFiles: any[] = Array.isArray(grn.proofFiles) ? grn.proofFiles : [];
  const hasProof = proofFiles.length > 0 || !!grn.deliveryNoteRef || !!grn.supplierInvoice || !!grn.noProofReason;
  const openProof = async (i: number) => {
    try { window.open(await getReceiptProofLink(grn.grnId, i), '_blank', 'noopener'); } catch (e: any) { message.error(e?.response?.data?.error || 'Could not open the file'); }
  };
  const outcomeText = (l: any) => {
    if (!l.rejectOutcome) return null;
    const bits = [OUTCOME_LABEL[l.rejectOutcome] || l.rejectOutcome];
    if (l.rejectOutcomeRef) bits.push(l.rejectOutcomeRef);
    if (l.rejectOutcomeDue) bits.push(`due ${dayjs(l.rejectOutcomeDue).format('D MMM')}`);
    if (l.rejectOutcomeAmount != null) bits.push(l.discountAlreadyPaid === false ? `pay ${money(Number(l.rejectOutcomeAmount))} less` : `vendor owes ${money(Number(l.rejectOutcomeAmount))}`);
    if (l.rejectOutcome === 'short_close') bits.push('closed on the PO');
    if (l.rejectOutcome === 'writeoff') {
      const cs = l.poLine?.cancelStatus;
      bits.push(cs === 'Pending' ? 'waiting for Ahmed (Cancellations)' : cs === 'Approved' ? 'approved by Ahmed' : cs === 'Rejected' ? 'not approved by Ahmed' : 'sent to Ahmed');
    }
    return bits.join(' · ');
  };
  const purchasePath: string | null = grn.poHeader?.purchasePath || null;
  // Who asked for the goods (requestedBy comes with the MR header on newer servers; absent = left blank).
  const requesters = Array.from(new Set(lines.map((l: any) => l.poLine?.mrLine?.mrHeader?.requestedBy?.displayName).filter(Boolean))).join(', ');
  const printSlip = () => {
    // 2026-10-22: layout per the approved mockup — "For" is the cost center code, requester on the handover line.
    const html = buildGrnSlipHtml({
      grnId: grn.grnId, poId: grn.poHeader?.poId, path: PATH_LABEL[grn.poHeader?.purchasePath] || null,
      vendor: grn.poHeader?.vendor?.name || '', department: grn.poHeader?.department?.name, receivedOn: grn.receivedOn,
      receivedBy: grn.receivedBy?.displayName, paidBy: grn.poHeader?.paymentMethod ? PAYMENT_LABEL[grn.poHeader.paymentMethod] : null,
      deliveryNote: grn.deliveryNoteRef, invoice: grn.supplierInvoice, notes: grn.notes, requester: requesters || null,
      lines: lines.map((l: any) => ({
        no: l.poLine?.lineNo, code: l.poLine?.mrLine?.catalogItem?.partNumber || l.poLine?.partNumber, item: l.poLine?.itemDescription || '',
        goesTo: l.poLine?.costCenter?.code || null,
        accepted: Number(l.acceptedQty), rejected: Number(l.rejectedQty), unit: l.poLine?.unit?.name, price: priceOf(l), outcome: outcomeText(l),
      })),
    }, { printedAt: dayjs().format('DD MMM YYYY [at] HH:mm'), printedBy: user?.displayName || '' });
    const w = window.open('', '_blank');
    if (!w) { message.error('Allow pop-ups for this site to print'); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };
  const canManage = canAccess('purchasing.grns', 'full');
  // Rejected goods step 1 (2026-10-24, mockup receipt 3–5): receipts saved by the new window show one case per
  // rejected line ("What happens next", no "Return (n)", D1); older receipts keep the Returns column as before (D5).
  const isNew = caseData ? caseData.newWindow : grn.receiveWindow === 'v2';
  const allCases = caseData?.cases || [];
  // A line is a rejection case when it has an outcome or a step to do; later returns of accepted goods (step 4a) are not.
  const rejCases = allCases.filter((c) => c.outcome || c.failures.length || c.closeRestNotSent);
  const caseByLine = new Map<number, ReceiptCase>(allCases.map((c) => [c.grnLineId, c]));
  const hasCases = isNew && rejCases.length > 0;
  const vendorName: string | null = caseData?.vendor ?? grn.poHeader?.vendor?.name ?? null;
  const summary = hasCases ? caseSummary(rejCases, vendorName) : null;
  // Step 4a: "Return goods" on each line of a receipt saved by the new window, for whoever can raise returns.
  const canReturn = isNew && !!returnable && canAccess('purchasing.returns', 'full');
  const since = caseData?.newReceivingSince ? dayjs(caseData.newReceivingSince).format('D MMM YYYY') : null;
  const choosingLine = choosing ? lines.find((l: any) => l.id === choosing.grnLineId) : null;
  // With the rejection columns the table must still fit a laptop card (~930 px) without sideways scrolling.
  const oldRej = !isNew && hasRejections;
  // Step 4a (mockup "Door & returns" 2): new receipts lose the Price column (the value stays) to make room for "Return goods".
  const W = hasCases
    ? { no: 40, item: 200, del: 0, acc: 100, price: canReturn ? 0 : 75, value: 90, goes: 112, rej: 80, next: 190, reason: 0, ret: 0, back: canReturn ? 112 : 0 }
    : oldRej
      ? { no: 40, item: 160, del: 75, acc: 75, price: 75, value: 85, goes: 130, rej: 75, next: 0, reason: 90, ret: 110, back: 0 }
      : { no: 50, item: canReturn ? 300 : 320, del: 0, acc: 130, price: canReturn ? 0 : 100, value: 110, goes: 170, rej: 0, next: 0, reason: 0, ret: 0, back: canReturn ? 112 : 0 };

  // 2026-10-22: layout per the approved mockup — # · Item · Accepted · Price · Value · Goes to; the rejection
  // columns only when something was rejected, inspection notes only when someone wrote them.
  const lineColumns: any[] = [
    {
      title: '#',
      key: 'poLineNo',
      width: W.no,
      align: 'center' as const,
      render: (_: any, record: any) => record.poLine?.lineNo || '-',
    },
    {
      title: 'Item',
      // Code (with the 36px picture), description and the MR it came from; 46px indent lines the MR up with the text.
      key: 'item',
      width: W.item,
      render: (_: any, record: any) => {
        const ci = record.poLine?.mrLine?.catalogItem;
        const mrHeader = record.poLine?.mrLine?.mrHeader;
        return (
          <div>
            <ItemCell
              partNumber={ci?.partNumber || record.poLine?.partNumber || null}
              description={record.poLine?.itemDescription || null}
              linkTo={ci?.id ? `/catalog/${ci.id}` : undefined}
              showThumb
              pictureUrl={ci?.id ? linePictures[ci.id]?.url : null}
              thumbItemId={ci?.id ?? null}
              onThumbError={ci?.id ? () => reloadLinePicture(ci.id) : undefined}
            />
            {mrHeader && (
              <div style={{ fontSize: 11.5, color: '#8a94a6', marginTop: 2, paddingLeft: 46 }}>
                <Link to={`/mrs/${mrHeader.id}`} style={{ color: 'inherit' }}>{mrHeader.mrId}</Link>
              </div>
            )}
          </div>
        );
      },
    },
    ...(!isNew && hasRejections ? [{
      title: 'Delivered',
      dataIndex: 'deliveredQty',
      width: W.del,
      align: 'right' as const,
      render: (val: any, record: any) => `${Number(val)} ${record.poLine?.unit?.name || ''}`.trim(),
    }] : []),
    {
      title: 'Accepted',
      dataIndex: 'acceptedQty',
      width: W.acc,
      align: 'right' as const,
      render: (val: any, record: any) => {
        const c = caseByLine.get(record.id);
        return (
          <div>
            {`${Number(val)} ${record.poLine?.unit?.name || ''}`.trim()}
            {isNew && Number(record.rejectedQty) > 0 && (
              <div style={{ fontSize: 11.5, color: '#8a94a6' }}>of {Number(record.deliveredQty)} delivered</div>
            )}
            {c?.outcome === 'discount' && (
              <div style={{ fontSize: 11.5, color: '#8a94a6' }}>{c.qty} kept at a discount</div>
            )}
          </div>
        );
      },
    },
    ...(W.price ? [{
      title: 'Price',
      key: 'price',
      width: W.price,
      align: 'right' as const,
      render: (_: any, record: any) => money(priceOf(record)),
    }] : []),
    {
      title: 'Value',
      key: 'value',
      width: W.value,
      align: 'right' as const,
      render: (_: any, record: any) => (
        <div>
          {money(Number(record.acceptedQty) * priceOf(record))}
          {!W.price && Number(record.acceptedQty) > 0 && <div style={{ fontSize: 11.5, color: '#8a94a6' }}>{money(priceOf(record))} each</div>}
        </div>
      ),
    },
    {
      title: 'Goes to',
      key: 'goesTo',
      width: W.goes,
      render: (_: any, record: any) => record.poLine?.costCenter
        ? <span><code>{record.poLine.costCenter.code}</code> {record.poLine.costCenter.name}</span>
        : <Text type="secondary">—</Text>,
    },
  ];

  if (hasCases) {
    lineColumns.push(
      {
        title: 'Rejected',
        dataIndex: 'rejectedQty',
        width: W.rej,
        align: 'right' as const,
        render: (val: any, record: any) => {
          const v = Number(val);
          const c = caseByLine.get(record.id);
          return v > 0 ? (
            <div>
              <Text type="danger">{`${v} ${record.poLine?.unit?.name || ''}`.trim()}</Text>
              {c?.reason && <div style={{ fontSize: 11.5, color: '#8a94a6' }}>{c.reason.toLowerCase()}</div>}
            </div>
          ) : <Text type="secondary">0</Text>;
        },
      },
      {
        title: 'What happens next',
        key: 'outcome',
        width: W.next,
        render: (_: any, record: any) => {
          const c = caseByLine.get(record.id);
          return c && (c.outcome || c.failures.length || c.closeRestNotSent)
            ? <CaseCell c={c} vendor={vendorName} canManage={canManage} onRetry={retryStep} onChooseAnother={setChoosing} />
            : <Text type="secondary">-</Text>;
        },
      },
    );
  } else if (!isNew && hasRejections) {
    // Receipts from before the new receiving: Rejected · Reason · Returns, raised here as before (D1, D5).
    lineColumns.push(
      {
        title: 'Rejected',
        dataIndex: 'rejectedQty',
        width: W.rej,
        align: 'right' as const,
        render: (val: any, record: any) => {
          const v = Number(val);
          return v > 0 ? <Text type="danger">{`${v} ${record.poLine?.unit?.name || ''}`.trim()}</Text> : <Text type="secondary">0</Text>;
        },
      },
      {
        title: 'Reason',
        dataIndex: 'rejectReason',
        width: W.reason,
        render: (val: string) => val ? (
          <Tag color="red">{val.replace(/_/g, ' ')}</Tag>
        ) : <Text type="secondary">-</Text>,
      },
    );
  }

  // Add return action column if user can manage and there are rejections (receipts before the new receiving only)
  if (!isNew && hasRejections && canManage) {
    lineColumns.push({
      title: 'Returns',
      key: 'returns',
      width: W.ret,
      render: (_: any, record: any) => {
        const rejected = Number(record.rejectedQty);
        const existingReturns = (record.returns || []).reduce((s: number, r: any) => s + Number(r.returnQty), 0);
        const remaining = rejected - existingReturns;

        if (rejected === 0) return <Text type="secondary">-</Text>;

        return (
          <Space direction="vertical" size={0}>
            {existingReturns > 0 && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {existingReturns} returned
              </Text>
            )}
            {remaining > 0 && (
              <Button
                type="link"
                size="small"
                icon={<RollbackOutlined />}
                onClick={() => openReturnModal(record)}
              >
                Return ({remaining})
              </Button>
            )}
          </Space>
        );
      },
    });
  }

  if (canReturn) {
    lineColumns.push({
      title: '',
      key: 'returnGoods',
      width: W.back,
      render: (_: any, record: any) => {
        const r = returnable!.get(record.id);
        const later = caseByLine.get(record.id)?.laterReturns || [];
        if (!r && !later.length) return null;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            {r && (r.canReturn > 0
              ? <Button size="small" icon={<RollbackOutlined />} onClick={() => setReturnGoods({ ...r, vendor: r.vendor || (grn.poHeader?.vendor ? { id: grn.poHeader.vendor.id, name: grn.poHeader.vendor.name } : null) })}>Return goods</Button>
              : <Text type="secondary" style={{ fontSize: 12 }}>All returned</Text>)}
            {later.map((x) => (
              <span key={x.id} style={{ fontSize: 11.5, color: '#8a94a6' }}>
                {x.code ? <Link to={`/returns?search=${encodeURIComponent(x.code)}`}>{x.code}</Link> : 'Return'} · {x.qty} back{x.status === 'Pending' ? ', to ship' : x.status === 'Shipped' ? ', shipped' : ''}
              </span>
            ))}
          </div>
        );
      },
    });
  }

  if (lines.some((l: any) => l.inspectionNotes)) {
    lineColumns.push({
      title: 'Inspection notes',
      dataIndex: 'inspectionNotes',
      width: 180,
      ellipsis: true,
      render: (val: string) => val || <Text type="secondary">-</Text>,
    });
  }

  // Flatten all returns for the returns table
  const allReturns = lines.flatMap((l: any) =>
    (l.returns || []).map((r: any) => ({
      ...r,
      poLineNo: l.poLine?.lineNo,
      itemDescription: l.poLine?.itemDescription,
    }))
  );

  const returnColumns: any[] = [
    {
      title: 'PO Line #',
      dataIndex: 'poLineNo',
      width: 90,
      align: 'center' as const,
    },
    {
      title: 'Item',
      dataIndex: 'itemDescription',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Return Qty',
      dataIndex: 'returnQty',
      width: 100,
      align: 'right' as const,
      render: (val: any) => Number(val),
    },
    {
      title: 'Reason',
      dataIndex: 'returnReason',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Date',
      dataIndex: 'returnDate',
      width: 120,
      render: (val: string) => dayjs(val).format('DD MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      align: 'center' as const,
      render: (val: string) => <Tag color={RETURN_STATUS_COLORS[val] || 'default'}>{val}</Tag>,
    },
    {
      title: 'Settlement',
      key: 'settlement',
      width: 160,
      render: (_: any, record: any) => {
        if (record.status === 'Credited') return <span><Tag color="green">Credit</Tag>{record.creditNoteRef || ''}</span>;
        if (record.status === 'Refunded') return <span><Tag color="cyan">Refund</Tag>{record.refundRef || ''}{record.refundAmount ? ` (${Number(record.refundAmount).toLocaleString()})` : ''}</span>;
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'By',
      key: 'returnedBy',
      width: 120,
      render: (_: any, record: any) => record.returnedBy?.displayName || '-',
    },
  ];

  if (canManage) {
    returnColumns.push({
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_: any, record: any) => {
        if (record.status === 'Pending') {
          return (
            <Space size={0} wrap>
              <Button type="link" size="small" icon={<SendOutlined />}
                onClick={() => setShipTarget({ returnId: record.id, code: record.returnCode, qty: Number(record.returnQty), unit: lines.find((l: any) => l.poLine?.lineNo === record.poLineNo)?.poLine?.unit?.name || null, item: record.itemDescription || '', vendor: grn.poHeader?.vendor?.name || null })}>
                Ship
              </Button>
              <Button type="link" size="small" icon={<DollarOutlined />} onClick={() => setSettleTarget({ kind: 'return', id: record.id })}>Settle</Button>
            </Space>
          );
        }
        if (record.status === 'Shipped') {
          return (
            <Button type="link" size="small" icon={<DollarOutlined />} onClick={() => setSettleTarget({ kind: 'return', id: record.id })}>
              Settle
            </Button>
          );
        }
        if (record.status === 'Credited' || record.status === 'Refunded' || record.status === 'Replaced') {
          return <Tag color="green">Complete</Tag>;
        }
        return null;
      },
    });
  }

  return (
    <div>
      {/* 2026-10-22: layout per the approved mockup — receipt id + path, PO · vendor · department under it, status on the right. */}
      <Row justify="space-between" align="middle" gutter={[12, 8]} style={{ marginBottom: 16 }}>
        <Col>
          <Space align="start" size={12}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/grns'))}>Back</Button>
            <div>
              <Space size={8} wrap>
                <Title level={4} style={{ margin: 0 }}>{grn.grnId}</Title>
                {purchasePath
                  ? <Tag color={purchasePath === 'direct' ? 'purple' : 'blue'}>{PATH_LABEL[purchasePath] || purchasePath}</Tag>
                  : <Tag>before redesign</Tag>}
              </Space>
              <div style={{ fontSize: 12.5, color: '#8a94a6' }}>
                {grn.poHeader?.poId ? <Link to={`/pos/${grn.poHeader.poId}`}>{grn.poHeader.poId}</Link> : '-'}
                {[grn.poHeader?.vendor?.name, grn.poHeader?.department?.name].filter(Boolean).map((t: string) => ` · ${t}`).join('')}
              </div>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Tag color="success" bordered={false} style={{ borderRadius: 999, margin: 0 }}>Received</Tag>
            <Button icon={<PrinterOutlined />} onClick={printSlip}>Print slip</Button>
            {isAdmin && <Button danger icon={<DeleteOutlined />} onClick={handleDeleteGRN}>Delete GRN</Button>}
          </Space>
        </Col>
      </Row>

      {/* 2026-10-22: layout per the approved mockup — received on, typed in, proof, paid by (not for vendor orders), notes. */}
      <Card style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2 }}>
          <Descriptions.Item label="Received on">
            {dayjs(grn.receivedOn).format('D MMM YYYY')}
            {grn.receivedOnReason && <div style={{ fontSize: 12, color: '#8a94a6' }}>{grn.receivedOnReason}</div>}
          </Descriptions.Item>
          <Descriptions.Item label="Typed in">
            {dayjs(grn.createdAt).format('D MMM YYYY HH:mm')}
            {grn.receivedBy?.displayName ? ` · ${grn.receivedBy.displayName}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Proof">
            {hasProof ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center' }}>
                {proofFiles.map((f: any, i: number) => (proofThumbs[i] ? (
                  <Button key={f.key ?? i} type="text" size="small" title={f.name} onClick={() => openProof(i)} style={{ padding: 0, height: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <img
                      src={proofThumbs[i]}
                      alt={f.name || 'Proof'}
                      onError={() => setProofThumbs((t) => { const n = { ...t }; delete n[i]; return n; })}
                      style={{ width: 30, height: 38, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb', display: 'block' }}
                    />
                    <span style={{ fontSize: 12, color: '#5c6370' }}>{f.name}</span>
                  </Button>
                ) : (
                  <Button key={f.key ?? i} type="link" size="small" icon={<PaperClipOutlined />} style={{ padding: 0 }} onClick={() => openProof(i)}>{f.name}</Button>
                )))}
                {grn.deliveryNoteRef && <span>DN {grn.deliveryNoteRef}</span>}
                {grn.supplierInvoice && <span>Invoice {grn.supplierInvoice}</span>}
                {grn.noProofReason && <span style={{ color: '#ad6800' }}>No receipt: {grn.noProofReason}</span>}
              </div>
            ) : <Text type="secondary">none</Text>}
          </Descriptions.Item>
          {purchasePath !== 'order' && (
            <Descriptions.Item label="Paid by">
              {purchasePath === 'direct'
                ? (grn.poHeader?.paymentMethod
                  ? PAYMENT_LABEL[grn.poHeader.paymentMethod] || grn.poHeader.paymentMethod
                  : <Text type="secondary">not recorded</Text>)
                : <Text type="secondary">not recorded before go-live</Text>}
            </Descriptions.Item>
          )}
          {grn.notes && <Descriptions.Item label="Notes">{grn.notes}</Descriptions.Item>}
        </Descriptions>
      </Card>

      {/* Summary Stats — 2026-10-22: layout per the approved mockup (Received per unit · Value · Rejected) */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Received" value={perUnit('acceptedQty')} valueStyle={{ fontSize: 18 }} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Value" value={`EGP ${money(receiptValue)}`} valueStyle={{ color: '#389e0d', fontSize: 18 }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic
              title="Rejected"
              value={perUnit('rejectedQty')}
              valueStyle={{ color: totalRejected > 0 ? '#ff4d4f' : undefined, fontSize: 18 }}
              prefix={totalRejected > 0 ? <CloseCircleOutlined /> : undefined}
            />
            {hasRejections && <div style={{ fontSize: 12, color: '#8a94a6' }}>{qualityPct}% quality</div>}
          </Card>
        </Col>
      </Row>

      {/* Rejected goods step 1 (2026-10-24): new receipts sum up their cases (mockup receipt 3); older receipts say
          they were recorded before the new receiving (mockup receipt 5). */}
      {summary && (
        <Alert
          type={summary.open ? 'warning' : 'info'}
          showIcon
          icon={summary.open ? <WarningOutlined /> : undefined}
          message={<span><b>{summary.title}</b>{summary.parts.length ? ` ${summary.parts.join(' · ')}.` : ''}</span>}
          style={{ marginBottom: 16 }}
        />
      )}
      {!isNew && (
        <Alert
          type="info"
          showIcon
          message={`Recorded before the new receiving${since ? ` (${since})` : ''}.${hasRejections ? ' Returns are raised here as before.' : ''}`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card title="GRN Lines" style={{ marginBottom: 16 }}>
        <ResponsiveTable
          card={{ header: ['item'], footer: isNew ? ['outcome', 'returnGoods'] : ['returns'] }}
          columns={lineColumns}
          dataSource={lines}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: lineColumns.reduce((s: number, c: any) => s + (c.width || 0), 0) }}
        />
      </Card>

      {!isNew && allReturns.length > 0 && (
        <Card title="Returns" style={{ marginBottom: 16 }}>
          <ResponsiveTable
            columns={returnColumns}
            dataSource={allReturns}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 1000 }}
          />
        </Card>
      )}

      {grn && (
        <Card style={{ marginTop: 16 }}>
          <EntityHistory entityType="GRN" entityId={grn.grnId} />
        </Card>
      )}

      <ChooseOutcomeWindow
        c={choosing}
        item={choosingLine?.poLine?.itemDescription || ''}
        vendor={vendorName}
        onClose={() => setChoosing(null)}
        onSaved={() => { setChoosing(null); refreshCases(); }}
      />

      {/* Return Modal */}
      <Modal
        title="Create Return for Rejected Items"
        open={returnModalOpen}
        onOk={handleCreateReturn}
        onCancel={() => setReturnModalOpen(false)}
        confirmLoading={returnLoading}
        okText="Create Return"
      >
        {returningLine && (
          <div>
            <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Item">{returningLine.poLine?.itemDescription}</Descriptions.Item>
              <Descriptions.Item label="Rejected Qty">{Number(returningLine.rejectedQty)}</Descriptions.Item>
              <Descriptions.Item label="Available to Return">{returningLine.maxReturn}</Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12 }}>
              <Text strong>Return Quantity:</Text>
              <InputNumber
                min={1}
                max={returningLine.maxReturn}
                value={returnForm.returnQty}
                onChange={(v) => setReturnForm((f) => ({ ...f, returnQty: v || 0 }))}
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong>Return Reason: <Text type="danger">*</Text></Text>
              <Input.TextArea
                rows={2}
                value={returnForm.returnReason}
                onChange={(e) => setReturnForm((f) => ({ ...f, returnReason: e.target.value }))}
                placeholder="e.g., Damaged fabric - wrong color shade"
                style={{ marginTop: 4 }}
              />
            </div>

            <div>
              <Text strong>Notes (optional):</Text>
              <Input.TextArea
                rows={2}
                value={returnForm.notes}
                onChange={(e) => setReturnForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes..."
                style={{ marginTop: 4 }}
              />
            </div>
          </div>
        )}
      </Modal>

      <ShipWindow target={shipTarget} onClose={() => setShipTarget(null)} onSaved={() => { setShipTarget(null); fetchGRN(); }} />
      <SettleWindow target={settleTarget} onClose={() => setSettleTarget(null)} onSaved={() => { setSettleTarget(null); fetchGRN(); }} />
      <ReturnGoodsWindow target={returnGoods} onClose={() => setReturnGoods(null)} onSaved={() => { setReturnGoods(null); refreshCases(); }} />

    </div>
  );
};

export default GRNView;
